import { ButtonDock } from '@focuslee/shared-mini-app';
import { Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getButtonThemeColors } from '../../helpers/theme';
import { CommonIcon } from '../common/CommonIcon';
import { CustomInput } from '../common/CustomInput';
import { CustomSelect } from '../common/CustomSelect';
import { Switch } from '../common/Switch';
import { Page } from '../Page';

const timeOptionIds = ['', 'custom', '15', '30', '60', '120'];

interface QuestFocusSettingsProps {
  initialSelectedTimeOption: string;
  initialFocusModeEnabled: boolean;
  onSave: (settings: { focusTime: string; enabled: boolean }) => void;
  onCancel: () => void;
}

export const QuestFocusSettings: FC<QuestFocusSettingsProps> = ({
  initialSelectedTimeOption,
  initialFocusModeEnabled,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation('common');
  const timeOptions = [
    { id: '', label: t('quest.focus.noTimer'), shortLabel: t('quest.focus.noTimer') },
    { id: 'custom', label: t('quest.focus.custom'), shortLabel: t('quest.focus.custom') },
    { id: '15', label: t('quest.focus.minutes', { count: 15 }), shortLabel: '15m' },
    { id: '30', label: t('quest.focus.minutes', { count: 30 }), shortLabel: '30m' },
    { id: '60', label: t('quest.focus.hours', { count: 1 }), shortLabel: '1h' },
    { id: '120', label: t('quest.focus.hours', { count: 2 }), shortLabel: '2h' },
  ];
  const adjustedSelectedTimeOption = 
    !initialSelectedTimeOption || initialSelectedTimeOption === '0'
      ? ''
      : timeOptionIds.includes(initialSelectedTimeOption)
      ? initialSelectedTimeOption
      : 'custom';
  const [selectedTimeOption, setSelectedTimeOption] = useState(adjustedSelectedTimeOption);
  const [customFocusTime, setCustomFocusTime] = useState(
    adjustedSelectedTimeOption === 'custom' && initialSelectedTimeOption 
      ? initialSelectedTimeOption 
      : '30'
  );
  const [focusModeEnabled, setFocusModeEnabled] = useState(initialFocusModeEnabled);

  const customInputRef = useRef<HTMLInputElement>(null);

  const handleTimeOptionChange = (value: string) => {
    setSelectedTimeOption(value);
  };

  const handleCustomTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setCustomFocusTime(value);
    }
  };

  const handleFocusModeChange = (enabled: boolean) => {
    setFocusModeEnabled(enabled);
  };

  const handleSave = useCallback(() => {
    onSave({
      focusTime: selectedTimeOption === 'custom' ? customFocusTime : selectedTimeOption,
      enabled: focusModeEnabled,
    });
  }, [selectedTimeOption, customFocusTime, focusModeEnabled]);

  const isValid = selectedTimeOption !== 'custom' || parseInt(customFocusTime) > 0;

  useEffect(() => {
    if (selectedTimeOption === 'custom' && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [selectedTimeOption]);

  return (
    <Page
      back={true}
      insetEnabled={false}
      backClick={onCancel}
      footer={
        <ButtonDock
          secondary={{ text: t('actions.cancel'), onClick: onCancel, navigation: 'back' }}
          main={{
            text: t('actions.save'),
            onClick: handleSave,
            enabled: isValid,
            bgColor: getButtonThemeColors(isValid).backgroundColor,
          }}
        />
      }
    >
      <Stack alignItems="center" justifyContent="center">
        <Text>{t('quest.focus.settings')}</Text>
      </Stack>
      <Stack 
        flexGrow={1}
        paddingLeft="10px" 
        paddingRight="10px" 
        paddingTop="4px" 
        gap={2}
        alignItems="center"
        justifyContent="center"
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" maxWidth="400px">
          <CustomSelect 
            selectedId={selectedTimeOption}
            setSelectedId={handleTimeOptionChange}
            options={timeOptions}
            text={(d) => d.label}
            renderSelected={(d) => d ? d.shortLabel : t('quest.focus.time')}
            renderSelectedBefore={() => <CommonIcon icon="timer" type="filled" color="var(--tg-theme-text-color)" size={20} />}
          />
          <Stack direction="row" alignItems="center" gap={1}>
            <Text>{t('quest.focus.title')}</Text>
            <Switch checked={focusModeEnabled} onChange={handleFocusModeChange} />
          </Stack>
        </Stack>
        {selectedTimeOption === 'custom' && (
          <Stack direction="row" alignItems="baseline" gap={1} marginTop={2}>
            <CustomInput
              ref={customInputRef}
              value={customFocusTime}
              onChange={handleCustomTimeChange}
              style={{ width: '50px' }}
              wrapperStyle={{ minHeight: 0 }}
            />
            <Text>{t('quest.minutes')}</Text>
          </Stack>
        )}
      </Stack>
    </Page>
  );
};
